import { HttpClient } from '@angular/common/http';
import { AbstractControl } from '@angular/forms';
import { CalendarDay, DocumentExt, ICoreAuthorizationDTO, ICoreMemberDTO, IDocumentDetailsDTO, IDocumentReferenceProductDTO, IDocumentResponseDTO, IServicingProviderDTO, MoveDocument, UploadDocument, UploadFile, UploadLetter } from '@app/core/model';
import { DocumentLetterType } from '@app/core/model/document-letter-type.model';
import { DocumentMoveAuthTableConfig } from '@app/documents/models/document-move-auth-table-config.model';
import { DocumentReferencesDMEPOSService } from '@app/products/dmepos/services/document-references.service';
import { DocumentsAGNotesReportDmePosService } from '@app/products/dmepos/services/documents-ag-notes-report.service';
import { DocumentsMoveDmePosService } from '@app/products/dmepos/services/documents-move.service';
import { DocumentsUploadDmePosService } from '@app/products/dmepos/services/documents-upload.service';
import { MemberDmePosService } from '@app/products/dmepos/services/member.service';
import { AuthorizationHHService } from '@app/products/hh/services/authorization.service';
import { DocumentReferencesHHService } from '@app/products/hh/services/document-references.service';
import { DocumentsAGNotesReportHHService } from '@app/products/hh/services/documents-ag-notes-report.service';
import { DocumentsMoveHHService } from '@app/products/hh/services/documents-move.service';
import { DocumentsUploadLetterHHService } from '@app/products/hh/services/documents-upload-letter.service';
import { DocumentsUploadHHService } from '@app/products/hh/services/documents-upload.service';
import { MemberHHService } from '@app/products/hh/services/member.service';
import { AuthorizationPACService } from '@app/products/pac/services/authorization.service';
import { DocumentReferencesPACService } from '@app/products/pac/services/document-references.service';
import { DocumentsAGNotesReportPacService } from '@app/products/pac/services/documents-ag-notes-report.service';
import { DocumentsMovePacService } from '@app/products/pac/services/documents-move.service';
import { DocumentsUploadLetterPACService } from '@app/products/pac/services/documents-upload-letter.service';
import { DocumentsUploadPACService } from '@app/products/pac/services/documents-upload.service';
import { ENotificationPacReportService } from '@app/products/pac/services/e-notification.service';
import { MemberPacService } from '@app/products/pac/services/member.service';
import { SharedLineSelectTableConfig } from '@app/shared/models/shared-line-select-table-config.model';
import { ENotificationBaseReportService } from '@app/shared/services/e-notification.base.service';
import { ShellService } from '@mynexus/mfe-core';
import { ProductNames, TableColumn, TenantNames } from '@mynexus/mynexus-ui-lib';
import { AuthorizationDMEPOSService } from '@products/dmepos/services/authorization.service';
import { DocumentsUploadLetterDmePosService } from '@products/dmepos/services/documents-upload-letter.service';
import { DocumentsDmeposService } from '@products/dmepos/services/documents.service';
import { DocumentsHHService } from '@products/hh/services/documents.service';
import { DocumentsPACService } from '@products/pac/services/documents.service';
import { ToastMessageOptions } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';

export abstract class DocumentsContextService {
  abstract getFilesMetaData(tenantId: string, authorizationId: string, authorizationNumber: string): Observable<IDocumentResponseDTO | IDocumentResponseDTO[]>;
  abstract updateDocumentCheck(tenantId: string, authorizationId: string, documentId: string, isUncheck: boolean);
  abstract uploadFile(tenantId: string, authorizationId: string, document: UploadFile[], isWidgetUpload: boolean);
  abstract moveFiles(tenantId: string, authorization: ICoreAuthorizationDTO, document: MoveDocument[]);
  abstract getFilesByName(tenantId: string, authorizationNumber: string, documentFileName: string): Observable<IDocumentResponseDTO[]>;
  abstract downloadZip(tenantId: string, authorizationByProductId: string, docMetaDataIds: string[]);
  abstract getFileById(tenantId: string, authorizationByProductId: string, id: string): Observable<DocumentExt>;
  abstract updateDocumentReceivedTime(tenantId: string, authorizationByProductId: string, document: IDocumentResponseDTO): Observable<IDocumentResponseDTO>;
  abstract getCalendarDay(startdate: string, numberofdays: number, isbusinessdatesonly: boolean): Observable<CalendarDay>;
  abstract uploadLetter(tenantId: string, authorization: ICoreAuthorizationDTO, data: UploadLetter): Observable<UploadLetter>;
  abstract deleteDocumentForMoveDocument(
    tenantId: string,
    authorizationByProductId: string,
    authorizationNumber: string,
    docMetaDataId: string,
    documentFileName: string,
  ): Observable<UploadDocument[]>;
  abstract mapAttachedFiles(files: UploadLetter[], config: {}): UploadLetter[];
}

export abstract class AuthorizationContextService {
  abstract getCoreAuthorization(tenantId: string, authorizationByProductId: string): Observable<ICoreAuthorizationDTO>;
  abstract mapAuthorizationDTO(authorizationDto: object): ICoreAuthorizationDTO;
}

export abstract class MemberContextService {
  abstract getMemberByAuthNumber(tenantId: string, authNumber: string): Observable<ICoreMemberDTO>;
  abstract mapMemberDTO(memberDto: object): ICoreMemberDTO;
}

export abstract class DocumentsMoveContextService {
  abstract getAuthorizationTableConfig(): TableColumn[];
  abstract mapAuthorizationTableData(authorizationDto: ICoreAuthorizationDTO[], memberDto: ICoreMemberDTO): DocumentMoveAuthTableConfig[];
  abstract getMoveDocumentData(tenantId: string, newAuthorizationNumber: string): Observable<[ICoreAuthorizationDTO, ICoreMemberDTO]>;
  abstract filterDocuments(documents: IDocumentResponseDTO[]): IDocumentResponseDTO[];
}

export abstract class DocumentsUploadLetterContextService {
  abstract isSingleLineSelection(letterType?: string): boolean;
  abstract getLetterTypes(tenantId: string, authorizationByProductId: string, tenantName?: string): Observable<DocumentLetterType[]>;
  abstract getLineTableConfig(): TableColumn[];
  abstract mapLineTableData(letterType: string, lineDto: object[], tenantName: TenantNames, authorization: ICoreAuthorizationDTO | null, servicingProvider: IServicingProviderDTO | null, authProviderPgpId: string | null): SharedLineSelectTableConfig[];
  abstract getNoLineMessage(documentType: string, tenantName: TenantNames): string;
  abstract getAuthorizationProvidersSummary(tenantId: string, authorizationByProductId: string): Observable<object>;
}

export abstract class DocumentsUploadContextService {
  abstract getModal(
    dialogService: DialogService,
    authorizationDto: ICoreAuthorizationDTO,
    lineData: object[],
    documents: IDocumentDetailsDTO[],
    memberDto: ICoreMemberDTO,
    message: ToastMessageOptions): DynamicDialogRef;
  abstract levelAdjustmentUpload(selectedDocumentTypes: string[]): void;
  abstract manageForbiddenDocumentType(
    documents: IDocumentResponseDTO[],
    documentsContextService: DocumentsContextService,
    documentTypes: IDocumentReferenceProductDTO[],
    authorizationStatus: string,
    productName: string,
    servicingProvider: any,
    createContinuedStayRequest: boolean,
  ): Observable<IDocumentReferenceProductDTO[]>;
  abstract setSelectionAbility(data: string, selectedTypes: AbstractControl): boolean;
  abstract forceContinuedStayRequest(selected: string[], selectedTypes: AbstractControl): boolean;
  abstract checkContinuedStayEligibility(authorizationByProductId: string): Observable<ToastMessageOptions>;
}

export abstract class DocumentsReferencesContextService {
  abstract getDocumentReferences(lineOfBusinessId: string, reference: string): Observable<IDocumentReferenceProductDTO[]>;
  abstract getUploadMessage(authorizationNumber: string , isExternal: boolean) : { severity: string; summary: string; detail: string; };
}

export abstract class DocumentsAGNotesReportContextService {
  abstract getInformationMessage(): string;
  abstract downloadAGNotes(tenantId: string, authorizationByProductId: string, lineIds: object[]);
}

export abstract class ENotificationReportContextService {
     abstract getENotificationReport(tenantId: string,authorizationByProductId: string);
}

// TODO: Remove PAC Commercial after LOB migration
export const documentContextFactory = (http: HttpClient, shell: ShellService) => {
  switch (shell.getAppName()) {
    case ProductNames.DMEPOS:
      return new DocumentsDmeposService(http, shell);
    case ProductNames.PACIM:
    case ProductNames.PacCommercial:
      return new DocumentsPACService(http, shell);
    case ProductNames.HomeHealth:
      return new DocumentsHHService(http, shell);
    default:
      throw new Error(`Unknown product name: ${shell.getAppName()}`);
  }
}

export const authorizationContextFactory = (http: HttpClient, shell: ShellService) => {
  switch (shell.getAppName()) {
    case ProductNames.DMEPOS:
      return new AuthorizationDMEPOSService(http, shell);
    case ProductNames.PACIM:
    case ProductNames.PacCommercial:
      return new AuthorizationPACService(http, shell);
    case ProductNames.HomeHealth:
      return new AuthorizationHHService(http, shell);
    default:
      throw new Error(`Unknown product name: ${shell.getAppName()}`);
  }
}

export const memberContextFactory = (http: HttpClient, shell: ShellService) => {
  switch (shell.getAppName()) {
    case ProductNames.DMEPOS:
      return new MemberDmePosService(http, shell);
    case ProductNames.PACIM:
    case ProductNames.PacCommercial:
      return new MemberPacService(http, shell);
    case ProductNames.HomeHealth:
      return new MemberHHService(http, shell);
    default:
      throw new Error(`Unknown product name: ${shell.getAppName()}`);
  }
}

export const documentsMoveContextFactory = (
  http: HttpClient,
  shell: ShellService,
) => {
  switch (shell.getAppName()) {
    case ProductNames.DMEPOS:
      const autorizationDMEPOSService = new AuthorizationDMEPOSService(http, shell);
      const memberDMEPOSService = new MemberDmePosService(http, shell);

      return new DocumentsMoveDmePosService(autorizationDMEPOSService, memberDMEPOSService);
    case ProductNames.PACIM:
    case ProductNames.PacCommercial:
      const autorizationPacService = new AuthorizationPACService(http, shell);
      const memberPacService = new MemberPacService(http, shell);

      return new DocumentsMovePacService(autorizationPacService, memberPacService);
    case ProductNames.HomeHealth:
      const authorizationHHService = new AuthorizationHHService(http, shell);
      const memberHHService = new MemberHHService(http, shell);

      return new DocumentsMoveHHService(authorizationHHService, memberHHService);
    default:
      throw new Error(`Unknown product name: ${shell.getAppName()}`);
  }
}

export const documentsUploadLetterContextFactory = (http: HttpClient, shell: ShellService) => {
  switch (shell.getAppName()) {
    case ProductNames.DMEPOS:
      return new DocumentsUploadLetterDmePosService(http, shell);
    case ProductNames.PACIM:
    case ProductNames.PacCommercial:
      return new DocumentsUploadLetterPACService(http, shell);
    case ProductNames.HomeHealth:
      return new DocumentsUploadLetterHHService(http, shell);
    default:
      throw new Error(`Unknown product name: ${shell.getAppName()}`);
  }
}

export const documentsUploadContextFactory = (http: HttpClient, shell: ShellService) => {
  switch (shell.getAppName()) {
    case ProductNames.DMEPOS:
      return new DocumentsUploadDmePosService(http, shell);
    case ProductNames.PACIM:
    case ProductNames.PacCommercial:
      return new DocumentsUploadPACService(http, shell);
    case ProductNames.HomeHealth:
      return new DocumentsUploadHHService(http, shell);
    default:
      throw new Error(`Unknown product name: ${shell.getAppName()}`);
  }
}

export const documentsReferencesContextFactory = (http: HttpClient, shell: ShellService) => {
  switch (shell.getAppName()) {
    case ProductNames.DMEPOS:
      return new DocumentReferencesDMEPOSService(http, shell);
    case ProductNames.PACIM:
    case ProductNames.PacCommercial:
      return new DocumentReferencesPACService(http, shell);
    case ProductNames.HomeHealth:
      return new DocumentReferencesHHService(http, shell);
    default:
      throw new Error(`Unknown product name: ${shell.getAppName()}`);
  }
}

export const documentsAGNotesReportContextFactory = (http: HttpClient, shell: ShellService) => {
  switch (shell.getAppName()) {
    case ProductNames.DMEPOS:
      return new DocumentsAGNotesReportDmePosService(http, shell);
    case ProductNames.PACIM:
    case ProductNames.PacCommercial:
      return new DocumentsAGNotesReportPacService(http, shell);
    case ProductNames.HomeHealth:
      return new DocumentsAGNotesReportHHService(http, shell);
    default:
      throw new Error(`Unknown product name: ${shell.getAppName()}`);
  }
}

export const ENotificationReportContextFactory = (http: HttpClient, shell: ShellService) => {
  switch (shell.getAppName()) {
    case ProductNames.PACIM:
    case ProductNames.PacCommercial:
      return new ENotificationPacReportService(http, shell);
    case ProductNames.HomeHealth:
      return new ENotificationBaseReportService(http, shell, ProductNames.HomeHealth );
    case ProductNames.DMEPOS:
       return new ENotificationBaseReportService(http, shell, ProductNames.DMEPOS);
    default:
      throw new Error(`Unknown product name: ${shell.getAppName()}`);
  }
}
